// Page: Survey
// Purpose: Public testimonial survey flow for a campaign. Loads campaign info,
// handles multi-step UX, optional client-side video compression, direct GCS
// upload via signed URL (Firebase fallback), and metadata submission.
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { get } from '../../../../api/core';
import { SERVER_URL } from '../../../../api/core';
import { themes } from '../../../../components/testimonials/survey/themes';
import { SurveyHeader } from '../../../../components/testimonials/survey/SurveyHeader';
import { ContactForm } from '../../../../components/testimonials/survey/ContactForm';
import { LocationForm } from '../../../../components/testimonials/survey/LocationForm';
import { VideoResponse } from '../../../../components/testimonials/survey/VideoResponse';
import { SuccessMessage } from '../../../../components/testimonials/survey/SuccessMessage';
import { NavigationButtons } from '../../../../components/testimonials/survey/NavigationButtons';
import { ProgressBar } from '../../../../components/testimonials/survey/ProgressBar';
import { ErrorScreen } from '../../../../components/testimonials/survey/ErrorScreen';
import AuthLoadingScreen from '../../../../components/shared/AuthLoadingScreen';
import SEOHead from '../../../../components/shared/SEOHead';
import { SurveyErrorModal } from '../../../../components/testimonials/survey/SurveyErrorModal';
import { SurveyProcessingModal, PROCESSING_STATE } from '../../../../components/testimonials/survey/SurveyProcessingModal';
import { SurveyErrorAlert } from '../../../../components/testimonials/survey/SurveyErrorAlert';
import { SurveyFullMessage } from '../../../../components/testimonials/survey/SurveyFullMessage';
import { compressVideo } from '../../../../components/testimonials/survey/utils/videoCompression';
import { uploadToGCS, uploadToFirebase, notifyServerOfUpload } from '../../../../components/testimonials/survey/utils/uploadUtils';

const steps = [
  { id: 'intro', title: 'Welcome' },
  { id: 'contact', title: 'Contact Info' },
  { id: 'location', title: 'Location' },
  { id: 'response', title: 'Your Response' },
  { id: 'success', title: 'Thank You' }
];

// Default colors for custom theme if not provided
const DEFAULT_GRADIENT_COLORS = {
  from: '#1a365d',
  via: '#3182ce',
  to: '#2c5282'
};
const DEFAULT_GRADIENT_DIRECTION = 'br';
const DEFAULT_TEXT_COLOR = '#ffffff';


export default function Survey() {
  const { id } = useParams();
  const [currentStep, setCurrentStep] = useState(() => {
    // Try to restore the current step from session storage
    const savedStep = sessionStorage.getItem(`survey_${id}_currentStep`);
    return savedStep || 'intro';
  });
  const [campaign, setCampaign] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [videoDuration, setVideoDuration] = useState(() => {
    // Try to restore video duration from session storage
    const savedDuration = sessionStorage.getItem(`survey_${id}_videoDuration`);
    return savedDuration ? parseInt(savedDuration, 10) : null;
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [currentProcessingStep, setCurrentProcessingStep] = useState(PROCESSING_STATE.IDLE);
  const errorTimeoutRef = useRef(null);
  const [repsLoaded, setRepsLoaded] = useState(false);
  const MAX_VIDEO_DURATION = 120; // 2 minutes in seconds
  const MIN_VIDEO_DURATION = 10;  // 10 seconds minimum
  const [startTime, setStartTime] = useState(() => {
    // Try to restore start time from session storage
    return sessionStorage.getItem(`survey_${id}_startTime`) || null;
  });
  const [uploadUrl, setUploadUrl] = useState(null); // Add state for GCS upload URL
  const [permanentUploadPath, setPermanentUploadPath] = useState(null); // Add state for permanent upload path
  const [formData, setFormData] = useState(() => {
    // Try to restore form data from session storage
    try {
      const savedFormData = sessionStorage.getItem(`survey_${id}_formData`);
      if (savedFormData) {
        const parsed = JSON.parse(savedFormData);
        
        // Handle the videoUrl specially since it needs to be recreated on page reload
        // (object URLs don't persist across page reloads)
        const formDataWithoutVideo = {
          ...parsed,
          videoUrl: null, // Will be recreated if videoFile exists
          videoFile: null // Can't persist File objects in sessionStorage
        };
        
        return formDataWithoutVideo;
      }
    } catch (err) {
      console.warn('Failed to restore form data from session storage:', err);
    }
    
    // Return default form data if nothing was saved or if there was an error
    return {
      firstName: '',
      lastName: '',
      email: '',
      zipCode: '',
      videoFile: null,
      videoUrl: null,
      originalVideoFile: null,
      currentQuestionIndex: 0,
      originalVideoSize: null,
      compressedVideoSize: null,
    };
  });
  const [currentQuestion, setCurrentQuestion] = useState(0);
  // Custom theme state variables
  const [customTheme, setCustomTheme] = useState(null);
  // Processing message state
  const [processingMessage, setProcessingMessage] = useState('');
  
  // Add a ref to track if we've started fetching the campaign
  const fetchingCampaignRef = useRef(false);
  // Add a ref to track if component is mounted
  const isMountedRef = useRef(true);

  // Add refs to track active XHR requests
  const activeXhrRef = useRef(null);
  const processingCancelledRef = useRef(false);

  // OPTIMIZED: Show basic layout immediately while loading
  const showBasicLayout = campaign || !isLoading;

  // Add a function to cancel the upload process
  const cancelUpload = () => {
    // Mark that processing was cancelled
    processingCancelledRef.current = true;
    
    // Abort any active XHR request
    if (activeXhrRef.current) {
      console.log('Aborting active XHR request');
      activeXhrRef.current.abort();
      activeXhrRef.current = null;
    }
    
    // Reset all states related to upload/compression
    setIsUploading(false);
    setCurrentProcessingStep(PROCESSING_STATE.IDLE);
    setCompressionProgress(0);
    setUploadProgress(0);
    
    // Important: reset any processed video data so we start from scratch next time
    if (formData.compressedVideoSize !== formData.originalVideoSize) {
      console.log('Resetting compressed video data after cancellation');
      // Reset to original video file
      setFormData(prev => ({
        ...prev,
        // Reset to original file
        videoFile: prev.originalVideoFile || prev.videoFile,
        compressedVideoSize: null,
        // Keep track if compression was attempted
        compressionCancelled: true
      }));
    }
    
    setError('Upload cancelled by user');
    
    // If there's any video element playing, stop it
    const videos = document.querySelectorAll('video');
    videos.forEach(video => {
      video.pause();
    });
    
    console.log('Upload process cancelled by user - all states reset');
  };


  useEffect(() => {
    // Set mounted ref to true when component mounts
    isMountedRef.current = true;
    
    // Simply fetch the campaign data directly without checking cache
      if (!fetchingCampaignRef.current) {
        fetchingCampaignRef.current = true;
        fetchCampaign();
    }
    
    // Cleanup function to mark component as unmounted
    return () => {
      isMountedRef.current = false;
    };
  }, [id]);

  useEffect(() => {
    if (campaign?.theme === 'custom' && campaign?.customColors) {
      // Create a custom theme object based on the campaign's customColors
      const customGradientColors = campaign.customColors.gradientColors || DEFAULT_GRADIENT_COLORS;
      const gradientDirection = campaign.customColors.gradientDirection || DEFAULT_GRADIENT_DIRECTION;
      const textColor = campaign.customColors.textColor || DEFAULT_TEXT_COLOR;
      
      // Convert direction format
      const direction = gradientDirection === 'br' ? '135deg' :
                         gradientDirection === 'r' ? '90deg' :
                         gradientDirection === 'b' ? '180deg' :
                         gradientDirection === 'tr' ? '45deg' :
                         gradientDirection === 'bl' ? '225deg' : '135deg';
      
      // Create gradient string
      const gradientString = `linear-gradient(${direction}, ${customGradientColors.from}, ${customGradientColors.via}, ${customGradientColors.to})`;
      
      // Set the custom theme
      setCustomTheme({
        background: '', // Will be applied as inline style
        text: '',  // Will be applied as inline style 
        subtext: '', // Will be applied as inline style
        border: 'border-white/20',
        input: 'bg-white/20',
        name: 'Custom Theme',
        gradientStyle: gradientString,
        textColor: textColor
      });
    }
  }, [campaign]);

  // Function to get background style for custom themes
  const getBackgroundStyle = () => {
    if (campaign?.theme === 'custom' && customTheme) {
      return { background: customTheme.gradientStyle };
    }
    return {};
  };

  // Function to get text style for custom themes
  const getTextStyle = () => {
    if (campaign?.theme === 'custom' && customTheme) {
      return { color: customTheme.textColor };
    }
    return {};
  };

  // Function to get subtext style (slightly transparent version of text color)
  const getSubtextStyle = () => {
    if (campaign?.theme === 'custom' && customTheme) {
      return { color: customTheme.textColor, opacity: 0.8 };
    }
    return {};
  };

  const fetchCampaign = async () => {
    try {
      console.log('Fetching campaign data for ID:', id);
      
      // Make ONE call to the correct API endpoint
      // Note the endpoint path structure: /campaign/campaigns/survey/:id
      const data = await get(`/campaign/campaigns/survey/${id}`);
      
      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;
      
      if (!data) {
        throw new Error('Campaign not found');
      }
      
      console.log('Campaign data received:', data);
      console.log('Survey full status:', data.surveyFull);
      
      // Store the upload URL if provided
      if (data.uploadUrl) {
        console.log('Upload URL received from server');
        setUploadUrl(data.uploadUrl);
      } else {
        console.warn('No upload URL provided by server');
      }

      // Store the permanent upload path if provided
      if (data.permanentUploadPath) {
        console.log('Permanent upload path received:', data.permanentUploadPath);
        setPermanentUploadPath(data.permanentUploadPath);
      } else {
        console.warn('No permanent upload path provided by server');
      }
      
      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;
      
      // Set the campaign data including surveyFull flag FIRST
      setCampaign(data);
      
      // Set loading to false IMMEDIATELY - don't wait for anything
      setIsLoading(false);
      
      // OPTIMIZED: Make image preloading completely non-blocking in background
      if (data.campaignImage) {
        const img = new Image();
        img.src = data.campaignImage;
        
        // Just start loading in background - don't block UI at all
        img.onload = () => {
          console.log('Campaign image preloaded successfully (background)');
        };
        img.onerror = () => {
          console.warn('Campaign image failed to load (background)');
        };
      }
      
    } catch (err) {
      console.error('Error fetching campaign:', err);
      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;
      setError(err.message);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (error) {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      
      // Don't auto-clear errors related to video length or file size
      const isVideoLengthError = error.includes("seconds long") || 
                               error.includes("must be at least 10 seconds") || 
                               error.includes("must be shorter than 2 minutes");
      
      const isFileSizeError = error.includes("file is too large") ||
                             error.includes("extremely large") ||
                             error.includes("quite large") ||
                             error.includes("file size") ||
                             error.includes("compression");
      
      if (!isVideoLengthError && !isFileSizeError) {
        // Only set a timeout for errors not related to video length or file size
        errorTimeoutRef.current = setTimeout(() => {
          setError(null);
        }, 5000);
      } else {
        // For important errors about video length or file size, log that we're keeping them visible
        console.log('Keeping persistent error visible:', error);
      }
    }
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, [error]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Function to check for previously uploaded video on page reload
  useEffect(() => {
    // We don't need to show the re-upload message anymore
    // Just silently handle the case where a video needs to be re-uploaded
    const restoredFormData = formData;
    if (restoredFormData.hadVideo && !formData.videoUrl) {
      // No need to show error message, user will just upload a new video as needed
      console.log('Video data was found in session storage but needs to be re-uploaded');
    }
  }, []);

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Track original file size
      const originalSize = file.size;
      console.log(`Original video size: ${(originalSize / 1024 / 1024).toFixed(2)} MB`);
      
      // Check if the file is extremely large and set warning flag
      const isExtremelyLarge = originalSize > 200 * 1024 * 1024; // 200MB (was 100MB)
      const isVeryLarge = originalSize > 100 * 1024 * 1024; // 100MB (was 50MB)
      
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = async function() {
        window.URL.revokeObjectURL(video.src);
        const duration = Math.round(video.duration);
        setVideoDuration(duration);

        // Always add the video to the UI regardless of length
        setFormData(prev => ({
          ...prev,
          videoFile: file,
          videoUrl: URL.createObjectURL(file),
          originalVideoSize: originalSize,
          compressedVideoSize: originalSize, // Initially set to original
          needsCompression: isExtremelyLarge, // Set flag if file is extremely large
          hadVideo: true // Mark that we have a video now
        }));

        // Show appropriate warnings based on size and duration
        if (isExtremelyLarge) {
          setError(`Your video file is extremely large (${(originalSize / 1024 / 1024).toFixed(1)} MB). Please use a shorter video or lower quality recording.`);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (isVeryLarge) {
          setError(`Your video file is quite large (${(originalSize / 1024 / 1024).toFixed(1)} MB). It might fail to upload. Consider recording a shorter video.`);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (duration > MAX_VIDEO_DURATION) {
          setError(null);
        } else if (duration < MIN_VIDEO_DURATION) {
          setError(`Your video is only ${duration} seconds long. Videos must be at least 10 seconds to submit.`);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          setError(null);
        }
      };

      video.onerror = function() {
        setError("There was an error loading your video. Please try a different file format or video.");
        window.scrollTo({ top: 0, behavior: 'smooth' });
      };

      video.src = URL.createObjectURL(file);
    }
  };

  const handleRemoveVideo = () => {
    setVideoDuration(null);
    setError(null);
    setFormData(prev => ({
      ...prev,
      videoFile: null,
      videoUrl: null,
      originalVideoSize: null,
      compressedVideoSize: null
    }));
  };

  // Function to handle survey start and record the time
  const handleSurveyStart = () => {
    // Record the exact time when user starts the survey
    const now = new Date().toISOString();
    setStartTime(now);
    // Continue with next step
    goToNextStep();
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset cancellation flag at the start of submission
    processingCancelledRef.current = false;
    
    // Basic validation
    if (!formData.videoFile) {
      if (currentStep === 'response') {
        setError('Please record or upload a video response before submitting');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    // Check if we have an upload URL
    if (!uploadUrl) {
      setError('No upload URL available. Please refresh the page and try again.');
      return;
    }

    // Validate video duration
    if (videoDuration > MAX_VIDEO_DURATION) {
      if (currentStep === 'response') {
        setError(`Video must be shorter than 2 minutes. Current length: ${Math.floor(videoDuration / 60)}:${String(videoDuration % 60).padStart(2, '0')}. Try using your device's built-in video trimming tool.`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    if (videoDuration < MIN_VIDEO_DURATION) {
      if (currentStep === 'response') {
        setError(`Video must be at least 10 seconds long. Current length: ${videoDuration} seconds.`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    // Check for extremely large files
    const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB file size limit (was 100MB)
    if (formData.videoFile.size > MAX_FILE_SIZE) {
      setError('Your video file is too large (over 200MB). Please record a shorter video or at a lower quality.');
      setFormData(prev => ({
        ...prev,
        needsCompression: true
      }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Initialize the upload process
    setIsUploading(true);
    setError('');
    setUploadProgress(0);
    setCompressionProgress(0);
    
    // Store original video file for potential restarts
    const originalVideoFile = formData.originalVideoFile || formData.videoFile;
    
    // Always save the original file for potential restart
    if (!formData.originalVideoFile) {
      setFormData(prev => ({
        ...prev,
        originalVideoFile: prev.videoFile
      }));
    }
    
    try {
      // STEP 1: Compress the video if needed
      let videoToUpload = formData.videoFile;
      let originalSize = formData.originalVideoSize || formData.videoFile.size;
      let compressedSize = originalSize;
      
      const needsCompression = formData.videoFile.size > 5 * 1024 * 1024 || 
                              formData.compressionCancelled || 
                              !formData.compressedVideoSize;
      
      if (needsCompression) {
        console.log('Starting video compression process...');
        setCurrentProcessingStep(PROCESSING_STATE.COMPRESSING);
        setCompressionProgress(0);
        
        try {
          // Compress the original file
          const fileToCompress = originalVideoFile;
          console.log(`Compressing video file (${(fileToCompress.size / 1024 / 1024).toFixed(2)} MB)`);
          
          const result = await compressVideo(fileToCompress, setCompressionProgress);
          
          // Check if process was cancelled during compression
          if (processingCancelledRef.current) {
            console.log('Process was cancelled during compression, aborting upload');
            return;
          }
          
          videoToUpload = result.compressedFile;
          originalSize = result.originalSize;
          compressedSize = result.compressedSize;
          
          console.log(`Compression complete. Original: ${(result.originalSize / 1024 / 1024).toFixed(2)} MB, Compressed: ${(result.compressedSize / 1024 / 1024).toFixed(2)} MB`);
          console.log(`Compression ratio: ${((1 - (result.compressedSize / result.originalSize)) * 100).toFixed(2)}%`);
          
          // Update form data with compression info
          setFormData(prev => ({
            ...prev,
            videoFile: videoToUpload,
            originalVideoSize: originalSize,
            compressedVideoSize: compressedSize,
            compressionCancelled: false
          }));
          
          // Show 100% complete for a moment
          setCompressionProgress(100);
          await new Promise(resolve => setTimeout(resolve, 800));
          
          // Check if cancelled during delay
          if (processingCancelledRef.current) {
            console.log('Process was cancelled after compression, aborting upload');
            return;
          }
          
        } catch (err) {
          console.error('Video compression failed:', err);
          
          // Check if process was cancelled
          if (processingCancelledRef.current) {
            console.log('Process was cancelled during compression error handling, aborting upload');
            return;
          }
          
          // Continue with original video if compression fails
          videoToUpload = originalVideoFile;
          compressedSize = originalSize;
        }
      } else {
        console.log('Using previously compressed video, skipping compression step');
      }

      // Check if process was cancelled before upload
      if (processingCancelledRef.current) {
        console.log('Process was cancelled before upload, aborting');
        return;
      }

      // STEP 2: Upload the video directly to Google Cloud Storage
      setCurrentProcessingStep(PROCESSING_STATE.UPLOADING);
      setUploadProgress(0);
      
      // Brief delay to ensure UI shows the state change
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Check if cancelled during delay
      if (processingCancelledRef.current) {
        console.log('Process was cancelled during transition to upload, aborting');
        return;
      }
      
      console.log(`Starting direct upload to GCS with file size: ${(videoToUpload.size / 1024 / 1024).toFixed(2)} MB`);
      
      // Perform the direct upload to GCS
      let uploadedVideoUrl;
      try {
        // Try direct upload method first (signed URLs work better with CORS)
        try {
          uploadedVideoUrl = await uploadToGCS(videoToUpload, uploadUrl, setUploadProgress, activeXhrRef, processingCancelledRef);
          console.log('Video successfully uploaded to GCS using direct method');
        } catch (directUploadError) {
          console.warn('Direct upload failed, trying Firebase SDK method:', directUploadError);
          // If direct upload fails, try Firebase SDK
          uploadedVideoUrl = await uploadToFirebase(videoToUpload, uploadUrl, setUploadProgress, activeXhrRef);
          console.log('Video successfully uploaded using Firebase SDK');
        }
        
        // Show 100% for a moment
        setUploadProgress(100);
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Check if cancelled during delay
        if (processingCancelledRef.current) {
          console.log('Process was cancelled after GCS upload, aborting metadata submission');
          return;
        }
        
      } catch (uploadError) {
        // Check if cancellation error
        if (processingCancelledRef.current) {
          console.log('Upload was cancelled, expected error');
          return;
        }
        
        console.error('Failed to upload video to GCS:', uploadError);
        
        // Check if it might be a size limit issue
        if (videoToUpload.size > 50 * 1024 * 1024) {
          showEntityTooLargeError();
        } else {
          setError(`Failed to upload video: ${uploadError.message || 'Network error'}`);
        }
        
        setIsUploading(false);
        setCurrentProcessingStep(PROCESSING_STATE.IDLE);
        return;
      }
      
      // STEP 3: Notify the server about the successful upload and submit metadata
      console.log('Submitting metadata to server...');
      
      try {
        // Create metadata object
        const metadata = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          zipCode: formData.zipCode,
          startTime: startTime,
          videoDuration: videoDuration * 1000, // Convert to milliseconds
          originalVideoSize: originalSize,
          compressedVideoSize: compressedSize
        };
        
        // We must use the permanent upload path instead of the temporary signed URL
        // This is the permanent GCS reference that won't expire
        if (!permanentUploadPath) {
          console.warn('No permanent upload path available, using temporary URL instead');
        }
        
        // Submit metadata to server with permanent upload path
        const result = await notifyServerOfUpload(uploadedVideoUrl, metadata, id, permanentUploadPath, SERVER_URL);
        
        // Check if cancelled after metadata submission
        if (processingCancelledRef.current) {
          console.log('Process was cancelled after metadata submission, not proceeding to success');
          return;
        }
        
        // Handle success - show success page
        console.log('Survey submission successful!', result);
        setCurrentStep('success');
        // Clear session data after successful submission
        clearSurveySession();
        
      } catch (metadataError) {
        // Check if cancellation
        if (processingCancelledRef.current) {
          console.log('Process was cancelled during metadata submission');
          return;
        }
        
        console.error('Failed to submit survey metadata:', metadataError);
        setError(`Failed to complete survey: ${metadataError.message}`);
      } finally {
        // Clean up regardless of outcome
        setIsUploading(false);
        setCurrentProcessingStep(PROCESSING_STATE.IDLE);
      }
      
    } catch (error) {
      // Only show error if not cancelled
      if (!processingCancelledRef.current) {
        console.error('Error in submission process:', error);
        setError(`An unexpected error occurred: ${error.message}`);
        
        // Cleanup
        setIsUploading(false);
        setCurrentProcessingStep(PROCESSING_STATE.IDLE);
      }
    }
  };

  const currentStepIndex = steps.findIndex(step => step.id === currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  const goToNextStep = () => {
    // Use the surveyFull flag from the campaign data
    if (campaign?.surveyFull === true && currentStep === 'intro') {
      console.log('Preventing navigation because survey is full');
      return;
    }
    
    if (!isLastStep) {
      setError(null);
      setCurrentStep(steps[currentStepIndex + 1].id);
      // Scroll to the top of the page when moving to the next step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousStep = () => {
    if (!isFirstStep) {
      setError(null);
      setCurrentStep(steps[currentStepIndex - 1].id);
      // Scroll to the top of the page when moving to the previous step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Add an effect to log campaign data when it changes
  useEffect(() => {
    if (campaign) {
      console.log('Campaign data updated:', campaign);
      console.log('Survey full status:', campaign.surveyFull);
      
      // Force explicit boolean conversion for consistency
      const isSurveyFull = campaign.surveyFull === true;
      console.log('isSurveyFull (explicit boolean):', isSurveyFull);
    }
  }, [campaign]);

  // Function to handle 413 Entity Too Large errors
  const showEntityTooLargeError = () => {
    setIsUploading(false);
    setCurrentProcessingStep(PROCESSING_STATE.IDLE);
    
    // Create specialized error message
    setError(`Your video file is too large for our server to process. 
      Please compress your video before uploading it again.`);
      
    // Set flag to show compression needed
    setFormData(prev => ({
      ...prev,
      needsCompression: true
    }));
    
    // Show error modal
    setShowErrorModal(true);
  };
  
  // State to track error modal visibility
  const [showErrorModal, setShowErrorModal] = useState(false);
  
  // Function to close error modal
  const closeErrorModal = () => {
    setShowErrorModal(false);
  };

  // Save current step to session storage whenever it changes
  useEffect(() => {
    if (id && currentStep) {
      sessionStorage.setItem(`survey_${id}_currentStep`, currentStep);
    }
  }, [currentStep, id]);

  // Save form data to session storage whenever it changes
  // Exclude the actual File objects and URLs which can't be serialized
  useEffect(() => {
    if (id) {
      try {
        // Create a copy without file objects and URLs
        const formDataForStorage = {
          ...formData,
          videoFile: null,
          videoUrl: null,
          originalVideoFile: null,
          // Keep track of whether we had a video
          hadVideo: !!formData.videoUrl,
          // Store video properties we need
          videoFileName: formData.videoFile?.name,
          videoFileType: formData.videoFile?.type,
          videoFileSize: formData.videoFile?.size,
        };
        
        sessionStorage.setItem(`survey_${id}_formData`, JSON.stringify(formDataForStorage));
        
        // Save video duration separately
        if (videoDuration) {
          sessionStorage.setItem(`survey_${id}_videoDuration`, videoDuration.toString());
        }
        
        // Save start time
        if (startTime) {
          sessionStorage.setItem(`survey_${id}_startTime`, startTime);
        }
      } catch (err) {
        console.warn('Failed to save form data to session storage:', err);
      }
    }
  }, [formData, videoDuration, startTime, id]);

  // Clear session storage when survey is successfully completed
  const clearSurveySession = () => {
    try {
      sessionStorage.removeItem(`survey_${id}_currentStep`);
      sessionStorage.removeItem(`survey_${id}_formData`);
      sessionStorage.removeItem(`survey_${id}_videoDuration`);
      sessionStorage.removeItem(`survey_${id}_startTime`);
      console.log('Survey session cleared');
    } catch (err) {
      console.warn('Failed to clear survey session:', err);
    }
  };

  if (error && !campaign) return <ErrorScreen error={error} />;

  // Choose which theme object to use
  const themeToUse = campaign?.theme === 'custom' ? customTheme : themes[campaign?.theme] || {};

  // Calculate the current progress to show in the UI (either compression or upload)
  const currentProgress = currentProcessingStep === PROCESSING_STATE.COMPRESSING ? compressionProgress : uploadProgress;
  const showProgress = isUploading && (currentProcessingStep === PROCESSING_STATE.COMPRESSING || uploadProgress > 0);

  return (
    <div 
      className={`min-h-screen ${campaign?.theme && campaign.theme !== 'custom' ? themes[campaign.theme].background : 'bg-gray-50'}`}
      style={campaign?.theme === 'custom' ? getBackgroundStyle() : {}}
    >
      {/* Dynamic SEO Meta Tags for Link Previews */}
      <SEOHead
        pageType="survey"
        dynamicTitle={campaign?.name ? `${campaign.name} - Share Your Story` : 'Share Your Story - Advocacy Lab'}
        dynamicDescription={campaign?.description || 'Share your voice and help amplify important stories. Record a quick video to make an impact.'}
        dynamicImage={campaign?.campaignImage || `${window.location.origin}/images/og-image.png`}
        customData={{
          organizationName: campaign?.organizationName || 'Advocacy Lab',
          campaignType: 'Video Testimonial Survey'
        }}
      />
      {/* Show loading indicator only when we have no campaign data and are still loading */}
      {isLoading && !campaign && (
        <AuthLoadingScreen />
      )}
      
      <ProgressBar 
        currentStepIndex={currentStepIndex} 
        totalSteps={steps.length} 
        theme={campaign?.theme} 
        themes={themes}
        customTheme={customTheme}
        getTextStyle={getTextStyle}
        // Hide progress bar if survey is full or we're uploading
        hidden={campaign?.surveyFull === true || isUploading}
      />

      {/* Error Modal - Fixed position overlay for compression errors */}
      <SurveyErrorModal 
        showErrorModal={showErrorModal} 
        closeErrorModal={closeErrorModal} 
      />

      {/* Processing Modal - Fixed position overlay */}
      <SurveyProcessingModal 
        isUploading={isUploading}
        cancelUpload={cancelUpload}
        currentProcessingStep={currentProcessingStep}
        compressionProgress={compressionProgress}
        uploadProgress={uploadProgress}
      />

      <div className={`w-full max-w-[1000px] mx-auto px-4 py-16 sm:px-6 sm:py-24 animate-fadeIn ${
        currentStep === 'response' 
          ? 'max-w-2xl lg:max-w-4xl' 
          : 'max-w-xl'
      } lg:px-8`}>
        {/* Only show top error message if we're not on the response page or not uploading */}
        <SurveyErrorAlert
          error={error}
          currentStep={currentStep}
          campaign={campaign}
          themes={themes}
          getTextStyle={getTextStyle}
          getSubtextStyle={getSubtextStyle}
        />

        <form onSubmit={handleSubmit}>
          {currentStep === 'intro' && campaign && (
            <>
              <SurveyHeader 
                campaign={campaign} 
                theme={campaign?.theme} 
                themes={themes} 
                goToNextStep={handleSurveyStart}  // Use the new handler instead of goToNextStep
                customTheme={customTheme}
                getTextStyle={getTextStyle}
                getSubtextStyle={getSubtextStyle}
                campaignId={id}
              />
              
              {/* Survey Full Message */}
              <SurveyFullMessage 
                campaign={campaign}
                themes={themes}
                getTextStyle={getTextStyle}
                getSubtextStyle={getSubtextStyle}
              />
            </>
          )}

          {currentStep === 'contact' && campaign && (
            <ContactForm 
              formData={formData} 
              handleInputChange={handleInputChange} 
              theme={campaign?.theme} 
              themes={themes}
              customTheme={customTheme}
              getTextStyle={getTextStyle}
              getSubtextStyle={getSubtextStyle}
              onRepsLoaded={setRepsLoaded}
            />
          )}

          {currentStep === 'location' && campaign && (
            <LocationForm 
              formData={formData} 
              handleInputChange={handleInputChange} 
              theme={campaign?.theme} 
              themes={themes}
              customTheme={customTheme}
              getTextStyle={getTextStyle}
              getSubtextStyle={getSubtextStyle}
              onRepsLoaded={setRepsLoaded}
            />
          )}

          {currentStep === 'response' && campaign && (
            <>
            <VideoResponse 
              formData={formData} 
              videoFile={formData.videoFile}
              videoUrl={formData.videoUrl}
              handleVideoUpload={handleVideoUpload} 
              handleRemoveVideo={handleRemoveVideo}
              videoDuration={videoDuration}
              campaign={campaign} 
              theme={campaign?.theme} 
              themes={themes}
              setCurrentQuestion={setCurrentQuestion}
              currentQuestion={currentQuestion}
              repsLoaded={repsLoaded}
              setRepsLoaded={setRepsLoaded}
              customTheme={customTheme}
              getTextStyle={getTextStyle}
              getSubtextStyle={getSubtextStyle}
            />
            </>
          )}

          {currentStep === 'success' && campaign && (
            <SuccessMessage 
              campaign={campaign} 
              theme={campaign?.theme} 
              themes={themes}
              customTheme={customTheme}
              getTextStyle={getTextStyle}
              getSubtextStyle={getSubtextStyle}
            />
          )}

          {campaign && (
            <NavigationButtons 
              currentStep={currentStep}
              isUploading={isUploading}
              uploadProgress={uploadProgress}
              goToPreviousStep={goToPreviousStep}
              goToNextStep={goToNextStep}
              handleSubmit={handleSubmit}
              theme={campaign?.theme}
              themes={themes}
              formData={formData}
              videoFile={formData.videoFile}
              videoDuration={videoDuration}
              repsLoaded={repsLoaded}
              customTheme={customTheme}
              getTextStyle={getTextStyle}
              getSubtextStyle={getSubtextStyle}
              surveyFull={campaign?.surveyFull === true}
              error={currentStep === 'response' ? error : null} // Pass error state to buttons if on response step
            />
          )}
        </form>
      </div>
    </div>
  );
}
